import React from 'react'
import { arrayOf, func, string, bool } from 'prop-types'
import {
  Button,
  Grid,
  Message,
  Modal,
  ModalContent
} from '@cobalt/cobalt-react-components'
import {
  MFA_SETTINGS_TITLE,
  MFA_SETTINGS_ENABLE,
  MFA_SETTINGS_DISABLE,
  MFA_SETTINGS_LOCAL_LOGIN_DISABLED,
  RECOVERY_OTP_CONFIG_ERROR,
  REVOKE_MFA_MECHANISM_ENFORCED_MESSAGE
} from '../../constants/ui.labels'
import SettingItem from '../Settings/SettingItem'
import RecoveryCodesOption from './RecoveryCodesOption'
import MechanismsList from './MechanismsList'
import mapError from '../../utils/mapError/mfa'
import { RECOVERY_OTP, Properties } from '../../constants/mfa'
import RecoveryCodesRenewConfirm from './RecoveryCodesRenewConfirm'
import ToastError from '../ToastError'
import WarningMessage from '../WarningMessage'

class MFASettings extends React.Component {
  state = {
    toastError: null,
    showMFAEnforcedWarning: false,
    showRecoveryCodesRenewConfirmation: false,
    recoveryOTPRegisterErrorCode: null,
    recoveryCodeList: null
  }
  isLastMfaMechanism = () =>
    this.props.enabledMFAMechanisms.filter(mfa => mfa !== RECOVERY_OTP)
      .length === 1

  componentDidUpdate(previousProps) {
    const {
      recoveryOTPRegisterErrorCode: currentError,
      recoveryCodeList
    } = this.props

    const {
      recoveryOTPRegisterErrorCode: previousError,
      recoveryCodeList: previousRecoveryCodeList
    } = previousProps

    if (
      currentError &&
      !previousError &&
      !this.state.showRecoveryCodesRenewConfirmation
    ) {
      this.setState({ toastError: this.createToastError() })
    }

    if (currentError !== previousError) {
      this.setState({
        recoveryOTPRegisterErrorCode: currentError
      })
    }

    if (recoveryCodeList !== previousRecoveryCodeList) {
      this.setState({ recoveryCodeList: recoveryCodeList })
    }
  }

  showRecoveryCodesRenewConfirmation = () =>
    this.setState({
      showRecoveryCodesRenewConfirmation: true
    })

  handleRecoveryCodesRenewConfirmDismiss = () =>
    this.setState({
      showRecoveryCodesRenewConfirmation: false,
      recoveryOTPRegisterErrorCode: null,
      recoveryCodeList: null
    })

  handleRevokeAllMFA = () => {
    const { onSelectMFAToRevoke, goToDisableMFAPage } = this.props
    onSelectMFAToRevoke(RECOVERY_OTP)
    goToDisableMFAPage()
  }

  handleRevokeMFA = mfaMechanism => {
    const { onSelectMFAToRevoke, mfaEnforced, goToDisableMFAPage } = this.props
    onSelectMFAToRevoke(mfaMechanism)
    if (this.isLastMfaMechanism() && mfaEnforced) {
      this.setState({
        showMFAEnforcedWarning: true
      })
    } else {
      goToDisableMFAPage()
    }
  }

  handleRevokeMFADismiss = () => {
    this.setState({
      showMFAEnforcedWarning: false
    })
    this.props.onDismissMFAMechanismConfirm()
  }

  renderActionButton = () => {
    const {
      mfaEnforced,
      onSetupMFA,
      hasMFAEnabled,
      isMFASetupInProgress,
      isLocalLoginEnabled
    } = this.props
    const state = isMFASetupInProgress ? Button.LOADING_STATE : null

    if (!hasMFAEnabled) {
      return (
        <Button
          primary
          inverted
          small
          state={state}
          onClick={onSetupMFA}
          disabled={!isLocalLoginEnabled || isMFASetupInProgress}
        >
          {MFA_SETTINGS_ENABLE}
        </Button>
      )
    }

    return (
      <Button
        danger
        inverted
        small
        state={state}
        disabled={mfaEnforced || isMFASetupInProgress}
        onClick={this.handleRevokeAllMFA}
      >
        {MFA_SETTINGS_DISABLE}
      </Button>
    )
  }

  renderMFAEnforcedWarning = () => {
    const title = Properties[this.props.mfaToRevoke].name
    return (
      <Modal
        basic
        dismissible={!this.props.isMFASetupInProgress}
        onDismiss={this.handleRevokeMFADismiss}
      >
        <ModalContent>
          <WarningMessage
            title={title}
            message={REVOKE_MFA_MECHANISM_ENFORCED_MESSAGE}
            onClick={this.handleRevokeMFADismiss}
          />
        </ModalContent>
      </Modal>
    )
  }

  renderRecoveryCodesRenewConfirmation = () => {
    const { isRecoveryCodesRenewInProgress, onRecoveryCodesRenew } = this.props
    const errorCode = this.state.recoveryOTPRegisterErrorCode
    return (
      <RecoveryCodesRenewConfirm
        onDismiss={this.handleRecoveryCodesRenewConfirmDismiss}
        onConfirm={onRecoveryCodesRenew}
        isRenewInProgress={isRecoveryCodesRenewInProgress}
        otpList={this.state.recoveryCodeList}
        errorMessage={
          errorCode && mapError(errorCode, RECOVERY_OTP_CONFIG_ERROR)
        }
      />
    )
  }

  renderMechanisms = () => {
    const { enabledMFAMechanisms, onSetupMFAMechanism } = this.props

    return (
      <Grid>
        <Grid.Group>
          <Grid.Column>
            <MechanismsList
              enabledMFAMechanisms={enabledMFAMechanisms}
              onSetup={onSetupMFAMechanism}
              onRevoke={this.handleRevokeMFA}
            />
          </Grid.Column>
        </Grid.Group>
        <Grid.Group verticalGutters={Grid.Group.HALF_VERTICAL_GUTTERS}>
          <Grid.Column>
            <React.Fragment />
          </Grid.Column>
        </Grid.Group>
        <Grid.Group>
          <Grid.Column>
            <RecoveryCodesOption
              onRenew={this.showRecoveryCodesRenewConfirmation}
            />
          </Grid.Column>
        </Grid.Group>
      </Grid>
    )
  }

  createToastError = () => (
    <ToastError
      title={mapError(
        this.props.recoveryOTPRegisterErrorCode,
        RECOVERY_OTP_CONFIG_ERROR
      )}
      onDismiss={this.dismissToastError}
    />
  )

  dismissToastError = () => this.setState({ toastError: null })

  renderMFADisabledWarning = () => (
    <Message warning>{MFA_SETTINGS_LOCAL_LOGIN_DISABLED}</Message>
  )

  render = () => (
    <React.Fragment>
      <SettingItem
        title={MFA_SETTINGS_TITLE}
        actionButton={this.renderActionButton()}
      >
        {this.state.showMFAEnforcedWarning && this.renderMFAEnforcedWarning()}
        {this.state.showRecoveryCodesRenewConfirmation &&
          this.renderRecoveryCodesRenewConfirmation()}
        {this.props.hasMFAEnabled && this.renderMechanisms()}
        {!this.props.isLocalLoginEnabled && this.renderMFADisabledWarning()}
      </SettingItem>
      {this.state.toastError}
    </React.Fragment>
  )
}

MFASettings.propTypes = {
  onSetupMFA: func.isRequired,
  onSetupMFAMechanism: func.isRequired,
  onRecoveryCodesRenew: func.isRequired,
  onDismissMFAMechanismConfirm: func.isRequired,
  onSelectMFAToRevoke: func.isRequired,
  enabledMFAMechanisms: arrayOf(string),
  mfaEnforced: bool.isRequired,
  hasMFAEnabled: bool.isRequired,
  isLocalLoginEnabled: bool.isRequired,
  isMFASetupInProgress: bool,
  recoveryOTPRegisterErrorCode: string,
  mfaToRevoke: string,
  isRecoveryCodesRenewInProgress: bool,
  recoveryCodeList: arrayOf(string),
  goToDisableMFAPage: func.isRequired
}

MFASettings.defaultProps = {
  isRecoveryCodesRenewInProgress: false,
  mfaToRevoke: RECOVERY_OTP
}

export default MFASettings
